import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { previewImage, previewTitle } from "aldoo-ra/CMS/preview"
import { GetCMSContent } from "../api"

export default function ContentSelectorDialog({
  isOpen,
  type,
  onSelect,
  onClose,
  parentValues,
  field = null,
  existingItems = [],
}) {
  const [searchQuery, setSearchQuery] = useState("") // For searching contents by name
  const [contentItems, setContentItems] = useState([]) // For holding the list of contents fetched from the API
  const [loading, setLoading] = useState(false) // To show a loading spinner when fetching data
  const searchInputRef = useRef(null) // Reference to the search input field

  // Function to load content items based on the type and search query
  const loadContentItems = async (query = "") => {
    setLoading(true) // Start loading

    const match = {
      deleted: false, // Fetch only non-deleted content
      "content.type": type, // Fetch content of the provided type
    }

    // Exclude the parent content, so we don't end up with circular references
    const parentValue = parentValues()
    if (parentValue) match._id = { $nin: [parentValue._id] }

    if (query) {
      match.$or = [
        { "content.name": { $regex: query, $options: "i" } }, // Search by name (case-insensitive)
        { "content.title": { $regex: query, $options: "i" } }, // Search by name (case-insensitive)
        { "content.label": { $regex: query, $options: "i" } }, // Search by name (case-insensitive)
        { "content.id": { $regex: query, $options: "i" } }, // Search by name (case-insensitive)
      ]
    }

    try {
      const result = await GetCMSContent({
        match,
        itemsPerPage: 1000,
      })
      
      let filteredData = result.data
      
      // Filter out existing items if duplicates are not allowed
      if (field && field.allowDuplicateSelection !== true && existingItems.length > 0) {
        const existingIds = existingItems.map(item => item._id).filter(id => id !== null)
        filteredData = result.data.filter(item => !existingIds.includes(item._id))
      }
      
      setContentItems(filteredData) // Set the fetched and filtered content items
    } catch (error) {
      console.error("Error loading content items:", error)
    } finally {
      setLoading(false) // End loading
    }
  }

  // Load content items whenever the dialog opens, search query changes, or existing items change
  useEffect(() => {
    if (!isOpen) return
    loadContentItems(searchQuery)
    // Focus on the search input field when the dialog opens
    if (searchInputRef.current) searchInputRef.current.focus()
  }, [isOpen, searchQuery, existingItems])

  // Handle content selection
  const handleSelectContent = (content) => {
    onSelect(content) // Pass the selected content back to the parent
    onClose() // Close the dialog
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      variant="admin"
      className="w-[600px] p-6"
    >
      <div className="flex flex-col text-admin_text h-full">
        <div className="flex-shrink-0">
          <h2 className="text-xl text-admin_text mb-4">
            Select a {type}
          </h2>

          {/* Search Input */}
          <Input
            variant="admin"
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name"
            icon={<MagnifyingGlassIcon className="w-6 h-6 text-admin_text_secondary" />}
            className="bg-admin_canvas rounded-full"
          />
        </div>

        {/* Content Items Listing */}
        <div className="mt-5 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper h-[60vh]">
          {loading ? (
            <p className="text-center text-admin_text_secondary">
              Loading...
            </p>
          ) : contentItems.length > 0 ? (
            contentItems.map((item) => (
              <div
                key={item._id}
                className="flex justify-between items-center border-b border-admin_bordered hover:bg-admin_canvas cursor-pointer p-3 mb-2"
                onClick={() => handleSelectContent(item)}
              >
                <div className="flex flex-row items-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full mr-4">
                    {previewImage(item) ? (
                      <img
                        src={previewImage(item)}
                        alt={`${item.content.name} icon`}
                        className="w-12 h-12 object-cover"
                      />
                    ) : (
                      item.content?.name?.charAt(0)?.toUpperCase() ||
                      item.content?.title?.charAt(0)?.toUpperCase() ||
                      "C"
                    )}
                  </div>

                  <div className="flex flex-col">
                    <div className="text-admin_text font-semibold">
                      {previewTitle(item)}
                    </div>

                    <div className="text-sm text-admin_text_secondary">
                      {item.content?.type}
                    </div>
                  </div>
                </div>

                <Button variant="link">
                  <ChevronRightIcon className="w-6 h-6 text-admin_text_secondary" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-center text-admin_text_secondary">
              No content found.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}
